import { TranslateService } from '@ngx-translate/core'
import { Component, Inject, Input } from '@angular/core'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { CloudConnection, Asset, BootstrapData, ClickGalleryLink, ClickGallery } from 'elements-sdk'

import { BOOTSTRAP_DATA } from 'api/providers'
import { IdentityService } from 'main/services/identity.service'
import { MessageBoxService, SocketService } from 'common/util/module'
import { BaseComponent } from 'common/helpers'
import { TaskState } from 'tasks/module'
import { CreateClickLinkModalComponent } from './createClickLinkModal.component'
import { CreateClickGalleryModalComponent } from './createClickGalleryModal.component'
import { APIService } from 'api/module'
import { NotifyService } from 'common/util/services/notify.service'

@Component({
  templateUrl: './clickSharingModal.component.pug',
})
export class ClickSharingModalComponent extends BaseComponent {
  progress: { asset: Asset, done: boolean }[] = []
  links: ClickGalleryLink[] = []
  galleries: ClickGallery[] = []
  gallery?: ClickGallery
  taskId?: string
  uploadedCount = 0
  galleryIsNew = false
  galleriesLoading = false
  cloudGalleryID = null
  currentAsset?: Asset
  connections = this.bootstrapData.cloudConnections
  notifyOnCompletion = true
  step = ''

  @Input() assets: Asset[]
  @Input() connection: CloudConnection

  constructor (
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    private api: APIService,
    private identity: IdentityService,
    private messageBox: MessageBoxService,
    private modalInstance: NgbActiveModal,
    private ngbModal: NgbModal,
    private socket: SocketService,
    private translate: TranslateService,
    private notify: NotifyService,
  ) {
    super()
    this.subs.add(
      socket.subject('cloud:upload-complete').subscribe(data => {
        this.progress.filter(x => x.asset.id === data.asset_id).forEach(progressItem => {
          progressItem.done = true
        })
        this.uploadedCount++
        if (!this.progress.every(x => x.done)) {
          this.currentAsset = this.progress.find(x => !x.done)?.asset
        }
      }),

      socket.subject('cloud:upload-failed').subscribe(data => {
        this.onError(data.reason)
      }),

      socket.subject('tasks:done').subscribe(data => {
        if (data.id === this.taskId) {
          if (data.state === TaskState.FINISHED || data.state === TaskState.WARNINGS) {
            this.finishUpload()
            this.currentAsset = undefined
          }
        }
      }),
    )
  }

  async ngOnInit () {
    this.progress = this.assets.map(x => ({
      asset: x,
      done: false,
    }))
    this.currentAsset = this.assets[0]
    this.startUpload()
    this.galleriesLoading = true
    try {
      this.galleries = await this.api.click.getAllClickGalleries({
        connectionId: this.connection.id.toString(),
      })
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant('Could not load galleries'), error)
    }

    this.galleriesLoading = false
  }

  onError (reason) {
    this.notify.error(this.translate.instant('Sharing failed'), reason)
    this.abort()
  }

  close () {
    this.modalInstance.dismiss()
  }

  async abort () {
    try {
      await this.messageBox.show({
        text: this.translate.instant('Abort the upload?'),
        positive: this.translate.instant('Abort'),
        negative: this.translate.instant('Continue'),
        danger: true,
      })
    } catch {
      return
    }
    if (this.taskId) {
      await this.api.click.abortClickUpload({ uploadId: this.taskId })
    }
    this.close()
  }

  selectGallery (gallery) {
    this.galleryIsNew = false
    if (gallery) {
      this.gallery = gallery
      if (this.step === 'done') {
        this.save()
      }
    } else {
      this.createGallery()
    }
  }

  async createGallery () {
    let name = `${this.assets.length} assets on ${new Date().toDateString()}`
    if (this.assets.length === 1) {
      name = this.assets[0].displayName
      if (name.includes('.')) {
        name = name.substring(0, name.lastIndexOf('.'))
      }
    }

    const m = this.ngbModal.open(CreateClickGalleryModalComponent)
    m.componentInstance.name = name
    m.componentInstance.connection = this.connection
    this.gallery = await m.result.catch(() => undefined)
    if (!this.gallery) {
      return
    }
    this.galleryIsNew = true
    if (this.step === 'done') {
      this.save()
    }
  }

  async save () {
    await this.api.click.addAssetsToClickGallery({
      connectionId: this.connection.id.toString(),
      id: this.gallery!.id.toString(),
      addAssetsToClickGallery: {
        assets: this.progress.map(x => x.asset.syncId),
      },
    })

    this.step = 'saved'
    this.sendEmails()
  }

  async sendEmails () {
    for (const link of this.links.filter(x => x.elementsUser?.email)) {
      await this.api.click.sendClickGalleryLinkEmail({
        connectionId: this.connection.id.toString(),
        linkId: link.id!.toString(),
      })
    }
  }

  background = async () => {
    await this.api.click.continueClickUploadInBackground({
      uploadId: this.taskId!,
      clickBackgroundUploadEndpointRequest: {
        gallery: this.gallery!.id,
        linksToSend: this.links.filter(x => x.elementsUser?.email).map(x => x.id!),
        notifyOnCompletion: this.notifyOnCompletion ? !!this.identity.user?.id : false,
      },
    })
    this.notify.info(this.translate.instant('Continuing in background'))
    this.modalInstance.close()
  }

  async startUpload () {
    try {
      this.step = 'uploading'
      this.uploadedCount = 0
      this.taskId = (await this.api.click.startClickUpload({
        clickStartUploadEndpointRequest: {
          connection: this.connection.id,
          assets: this.assets.map(x => x.id),
        },
      })).id
    } catch (error) {
      this.onError(error.message)
    }
  }

  finishUpload () {
    if (this.gallery) {
      this.save()
    } else {
      this.step = 'done'
    }
  }

  async createLink () {
    const m = this.ngbModal.open(CreateClickLinkModalComponent)
    m.componentInstance.gallery = this.gallery
    m.componentInstance.connection = this.connection
    const link = await m.result.catch(() => null)
    if (!link) {
      return
    }
    this.links.push(link)
    if (this.step === 'saved' && link.elementsUser?.email && m.componentInstance.sendEmail) {
      this.api.click.sendClickGalleryLinkEmail({
        connectionId: this.connection.id.toString(),
        linkId: link.id!.toString(),
      })
    }
  }

  async deleteLink (link) {
    await this.messageBox.deletePrompt(this.translate.instant('link'), '')
    await this.api.click.deleteClickGalleryLink({
      connectionId: this.connection.id.toString(),
      id: link.id,
    })
    this.links = this.links.filter(x => x !== link)
  }
}
